import React, { useState } from "react";
import {
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import GetAppIcon from "@mui/icons-material/GetApp";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { format } from "date-fns";

// ─── Number → Indian Currency Words ──────────────────────────────────────────
export function convertNumberToWords(amount: number): string {
  if (!amount || amount === 0) return "Zero Rupees Only";
  const sd = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const dd = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tm = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const lt1k = (n: number): string => {
    let s = "";
    if (n >= 100) { s += sd[Math.floor(n / 100)] + " Hundred "; n %= 100; }
    if (n >= 10 && n < 20) s += dd[n - 10] + " ";
    else if (n >= 20) s += tm[Math.floor(n / 10)] + " " + sd[n % 10] + " ";
    else if (n > 0) s += sd[n] + " ";
    return s;
  };
  let rv = Math.floor(Math.abs(amount));
  const pv = Math.round((Math.abs(amount) - rv) * 100);
  let rs = "";
  if (rv >= 10000000) { rs += lt1k(Math.floor(rv / 10000000)) + "Crore "; rv %= 10000000; }
  if (rv >= 100000) { rs += lt1k(Math.floor(rv / 100000)) + "Lakh "; rv %= 100000; }
  if (rv >= 1000) { rs += lt1k(Math.floor(rv / 1000)) + "Thousand "; rv %= 1000; }
  if (rv > 0) rs += lt1k(rv);
  let result = rs.trim() + " Rupees";
  if (pv > 0) result += " and " + lt1k(pv).trim() + " Paisa";
  return result + " Only";
}

// ─── Format Indian currency helper ──────────────────────────────────────────
export function formatIndianCurrency(num: number): string {
  if (isNaN(num) || num === null || num === undefined) return "0.00";
  const parts = num.toFixed(2).split(".");
  let lastThree = parts[0].substring(parts[0].length - 3);
  const otherNumbers = parts[0].substring(0, parts[0].length - 3);
  if (otherNumbers !== "") lastThree = "," + lastThree;
  const res = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
  return res + "." + parts[1];
}

export interface TransportationDetailsReportPreviewProps {
  open: boolean;
  onClose: () => void;
  data: any;
  handleClickOpenSendEmail?: (open: boolean, itemData: any) => void;
  handleClickOpenSendWhatsapp?: (open: boolean, itemData: any) => void;
  itemData?: any;
  template?: string;
}

export default function TransportationDetailsReportPreview(props: TransportationDetailsReportPreviewProps) {
  const { onClose, data, handleClickOpenSendEmail, handleClickOpenSendWhatsapp, itemData } = props;
  const [zoom, setZoom] = useState<number>(100);
  const [selectedCopy, setSelectedCopy] = useState<string>("Both");
  const [isDownloading, setIsDownloading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!data) return null;

  const header = data.header || data.Header || {};
  const details = data.detailCollection || data.DetailCollection || [];
  const taxList = data.taxSummaryCollection || data.TaxSummaryCollection || [];
  const companyInfo = data.companyInfo || data.CompanyInfo || {};

  const fmtDate = (v: any) => {
    if (!v) return "";
    try {
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : format(d, "dd-MM-yyyy");
    } catch {
      return v;
    }
  };

  const invoiceDate = fmtDate(header.saleDate || header.SaleDate);
  const dueDate = fmtDate(header.dueOnDate || header.DueOnDate);

  // Derive date range for transport statement
  const validTripDates = details
    .map((i: any) => i.lrDate || i.LrDate || i.tripDate || i.TripDate)
    .filter(Boolean)
    .map((d: any) => new Date(d))
    .filter((d: any) => !isNaN(d.getTime()));

  let dateRangeStr = "";
  if (validTripDates.length > 0) {
    const minD = new Date(Math.min(...validTripDates.map((d: Date) => d.getTime())));
    const maxD = new Date(Math.max(...validTripDates.map((d: Date) => d.getTime())));
    dateRangeStr = `${format(minD, "dd-MM-yyyy")} To ${format(maxD, "dd-MM-yyyy")}`;
  } else if (header.fromDate && header.toDate) {
    dateRangeStr = `${fmtDate(header.fromDate)} To ${fmtDate(header.toDate)}`;
  } else {
    dateRangeStr = invoiceDate;
  }

  // Totals calculations
  const totalActQty = details.reduce((s: number, i: any) => s + Number(i.actualQuantity || i.ActualQuantity || 0), 0);
  const totalBillQty = details.reduce((s: number, i: any) => s + Number(i.billQuantity || i.BillQuantity || 0), 0);
  const totalAmt = details.reduce(
    (s: number, i: any) =>
      s + Number(i.finalAmount || i.FinalAmount || 0) + Number(i.waitingCharges || i.WaitingCharges || 0),
    0
  );

  const firstTax = taxList[0] || {};
  const totalTaxableA =
    taxList.reduce((s: number, i: any) => s + Number(i.totalTaxableAmount || i.TotalTaxableAmount || 0), 0) || totalAmt;

  const totalCGST = taxList.reduce(
    (s: number, i: any) => s + Number(i.totalCentralTaxAmount || i.TotalCentralTaxAmount || 0),
    0
  );
  const totalSGST = taxList.reduce(
    (s: number, i: any) => s + Number(i.totalStateTaxAmount || i.TotalStateTaxAmount || 0),
    0
  );
  const totalIGST = taxList.reduce(
    (s: number, i: any) => s + Number(i.totalIntegratedTaxAmount || i.TotalIntegratedTaxAmount || 0),
    0
  );

  const taxType = firstTax.taxTypeId || 1;
  const isIntraState = Number(taxType) === 1;
  const totalTax = isIntraState ? totalCGST + totalSGST : totalIGST;

  const subTotal = firstTax.saleSubTotal || firstTax.SaleSubTotal || totalAmt;
  const roundOff = Number(firstTax.roundOff || firstTax.RoundOff || 0);
  const netTotal = firstTax.saleFinalGrandTotal || firstTax.SaleFinalGrandTotal || (subTotal + totalTax + roundOff);

  const taxRate = Number(firstTax.gstRate || firstTax.taxRate || 18);
  const cgstRate = Number(firstTax.centralTaxRate || taxRate / 2);
  const sgstRate = Number(firstTax.stateTaxRate || taxRate / 2);
  const hsnCode = firstTax.hsnCode || firstTax.HsnCode || "";
  const taxCode = firstTax.taxCode || firstTax.TaxCode || "";

  const shipping = firstTax.saleShippingTotal || firstTax.SaleShippingTotal || 0;
  const lrAdjustmentAmount = Number(firstTax.lrAdjustmentAmount || firstTax.LrAdjustmentAmount || 0);
  const additionalChargesLableName = firstTax.additionalChargesLableName || firstTax.AdditionalChargesLableName || "Adjustment";
  const additionalChargesLableValue = Number(firstTax.additionalChargesLableValue || firstTax.AdditionalChargesLableValue || 0);

  const upiId = header.upiId || "";
  const upiName = encodeURIComponent(companyInfo.consumerName || companyInfo.ConsumerName || "Company");
  const upiUrl = `upi://pay?pa=${upiId}&pn=${upiName}&am=${netTotal.toFixed(2)}&cu=INR`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(upiUrl)}`;

  const accountHolderName = companyInfo.accountHolderName || header.accountHolderName || "";
  const accountName = companyInfo.accountNumber || companyInfo.accountNo || companyInfo.AccountNumber || header.accountNumber || header.accountNo || header.AccountNo || header.accountName || "";
  const bankAddress = companyInfo.bankAddress || header.bankAddress || "";

  const logoImage =
    companyInfo.logoServerImage ||
    companyInfo.LogoServerImage ||
    companyInfo.logoPath ||
    companyInfo.logo ||
    companyInfo.logoData ||
    "";

  // ── Print handler ─────────────────────────────────────────────────────────
  const handlePrint = () => {
    const orig = document.title;
    document.title = `${header.saleNumber || header.SaleNumber || "invoice"}${
      header.buyerName || header.BuyerName ? " - " + (header.buyerName || header.BuyerName) : ""
    }`;
    window.print();
    setTimeout(() => {
      document.title = orig;
    }, 1200);
  };

  // ── PDF Download handler ──────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const loadScript = (src: string) =>
        new Promise<void>((res, rej) => {
          if (document.querySelector(`script[src="${src}"]`)) {
            res();
            return;
          }
          const s = document.createElement("script");
          s.src = src;
          s.onload = () => res();
          s.onerror = () => rej();
          document.head.appendChild(s);
        });
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      const el = document.getElementById("transport-details-report-print-node");
      if (!el) throw new Error("Print node not found");

      el.style.position = "fixed";
      el.style.top = "-9999px";
      el.style.left = "0";
      el.style.display = "block";
      el.style.width = "794px";
      await new Promise((r) => setTimeout(r, 200));

      const canvas = await (window as any).html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: el.scrollWidth,
        height: el.scrollHeight,
      });

      el.style.position = "";
      el.style.top = "";
      el.style.left = "";
      el.style.display = "none";
      el.style.width = "";

      const { jsPDF } = (window as any).jspdf;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210,
        ph = 297;
      const ih = (canvas.height * pw) / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      let left = ih,
        pos = 0;
      pdf.addImage(imgData, "JPEG", 0, pos, pw, ih);
      left -= ph;
      while (left > 0) {
        pos = left - ih;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, pos, pw, ih);
        left -= ph;
      }

      const fn = `${header.saleNumber || header.SaleNumber || "invoice"}${
        header.buyerName || header.BuyerName ? " - " + (header.buyerName || header.BuyerName) : ""
      }.pdf`;
      pdf.save(fn);
    } catch (e) {
      console.error(e);
      handlePrint();
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Print CSS ─────────────────────────────────────────────────────────────
  const printCSS = `
    @media print {
      @page { size: A4 portrait; margin: 0mm; }
      html, body {
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        position: static !important;
      }
      body > *:not(.MuiDialog-root) { display: none !important; }
      .MuiBackdrop-root, .no-print, .no-print * { display: none !important; }
      .MuiDialog-root, .MuiDialog-container, .MuiDialog-paper, .MuiDialogContent-root {
        display: block !important; position: static !important;
        width: 100% !important; max-width: 100% !important;
        height: auto !important; min-height: auto !important; max-height: none !important;
        margin: 0 !important; padding: 0 !important;
        box-shadow: none !important; border: none !important;
        overflow: visible !important; background: transparent !important;
      }
      #transport-details-report-print-node {
        display: block !important; width: 100% !important; max-width: 100% !important;
        height: auto !important; min-height: auto !important; max-height: none !important;
        position: static !important; overflow: visible !important;
        transform: none !important; padding: 0 !important; margin: 0 !important;
        box-shadow: none !important; font-size: 8pt !important;
      }
      #transport-details-report-print-node td, #transport-details-report-print-node th {
        white-space: normal !important; word-break: break-word !important;
        overflow: visible !important; font-size: inherit !important;
      }
      #transport-details-report-preview-node { display: none !important; }
      html, body, div, span, p { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  `;

  // ── Render 2-Page Invoice Report ─────────────────────────────────────────
  const renderInvoice = (nodeId: string, compact: boolean) => {
    const S = (px: number) => (compact ? `${(px * 0.75).toFixed(1)}pt` : `${px}px`);
    const primaryColor = "#000000";
    const accentColor = "#555555";
    const headerBg = "#f0f0f0";
    const borderStyle = "1px solid #cccccc";
    const tableHeaderBg = "#f0f0f0";
    const tableHeaderTextColor = "#000000";
    const BORDER = borderStyle;
    const BORDER_BOLD = "2px solid #000000";

    const TH: React.CSSProperties = {
      fontSize: S(9),
      fontWeight: "bold",
      padding: "3px 4px",
      borderRight: BORDER,
      borderBottom: BORDER,
      backgroundColor: tableHeaderBg,
      color: tableHeaderTextColor,
      textAlign: "left",
      lineHeight: 1.3,
      verticalAlign: "top",
      whiteSpace: "normal",
      wordBreak: "break-word",
    };
    const TD: React.CSSProperties = {
      fontSize: S(9),
      fontWeight: 500,
      padding: "3px 4px",
      borderRight: BORDER,
      borderBottom: BORDER,
      lineHeight: 1.3,
      verticalAlign: "top",
      whiteSpace: "normal",
      wordBreak: "break-word",
    };

    const fmt2 = (n: number) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const ROOT_PAGE: React.CSSProperties = {
      fontFamily: "Calibri, Arial, sans-serif",
      fontSize: S(10),
      color: "#000",
      backgroundColor: "#fff",
      padding: compact ? "10mm 10mm 3mm 10mm" : "10mm 10mm 15mm 10mm",
      boxSizing: "border-box",
      width: "100%",
      height: compact ? "297mm" : "1120px",
      minHeight: compact ? "297mm" : "1120px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      position: "relative",
      ...(compact
        ? {}
        : {
            boxShadow: "0px 6px 18px rgba(0,0,0,0.38)",
          }),
    };

    const copyTypes =
      selectedCopy === "Both"
        ? ["Original Copy", "Duplicate Copy"]
        : selectedCopy === "Original"
        ? ["Original Copy"]
        : ["Duplicate Copy"];

    const buyerName = header.buyerName || header.BuyerName || "";
    const buyerAddressLines = [
      [
        header.buyerAddressPart1 || header.buyerAddressPart1Name || header.buyerAddress1 || header.BuyerAddress1 || header.buyerAddress || header.BuyerAddress,
        header.buyerAddressPart2 || header.buyerAddressPart2Name || header.buyerAddress2 || header.BuyerAddress2 || header.BuyerAddressPart2,
      ]
        .filter(Boolean)
        .join(", "),
      [
        header.buyerCity || header.BuyerCity,
        header.buyerState || header.BuyerState,
        header.buyerPostalCode || header.buyerPincode || header.BuyerPostalCode || header.BuyerPincode,
      ]
        .filter(Boolean)
        .join(", "),
      header.buyerCountry || header.BuyerCountry,
    ].filter(Boolean);

    const buyerAddress = buyerAddressLines.length > 0 ? buyerAddressLines.join(", ") : (header.buyerAddress || header.BuyerAddress || "");
    const buyerGstin =
      header.buyerTaxNumber ||
      header.buyerGstin ||
      header.BuyerTaxNumber ||
      header.BuyerGstin ||
      header.taxNumber ||
      header.TaxNumber ||
      header.gstNumber ||
      header.GstNumber ||
      header.gstin ||
      header.Gstin ||
      "";
    const placeOfSupply = header.placeOfSupply || header.PlaceOfSupply || header.buyerState || companyInfo.state || "";
    const saleNumber = header.saleNumber || header.SaleNumber || "";

    const firmName =
      companyInfo.firmName ||
      companyInfo.FirmName ||
      companyInfo.consumerName ||
      companyInfo.ConsumerName ||
      header.firmName ||
      header.FirmName ||
      header.companyName ||
      header.CompanyName ||
      "";
    const companyGstin =
      companyInfo.taxNumber ||
      companyInfo.taxNo ||
      companyInfo.TaxNumber ||
      companyInfo.branchTaxNumber ||
      companyInfo.BranchTaxNumber ||
      header.branchTaxNumber ||
      header.BranchTaxNumber ||
      "";
    const msmeNo =
      companyInfo.msmeNumber ||
      companyInfo.msmeNo ||
      companyInfo.MsmeNumber ||
      companyInfo.branchUdyamNumber ||
      companyInfo.BranchUdyamNumber ||
      header.msmeNo ||
      header.branchUdyamNumber ||
      header.BranchUdyamNumber ||
      "";
    const msmeCategory = companyInfo.msmeCategory || companyInfo.MsmeCategory || header.msmeCategory || "Micro";
    const officeAddr =
      [
        companyInfo.addressPart1 || companyInfo.AddressPart1,
        companyInfo.addressPart2 || companyInfo.AddressPart2,
        companyInfo.city || companyInfo.City,
        companyInfo.state || companyInfo.State,
        companyInfo.pincode || companyInfo.Pincode,
      ]
        .filter(Boolean)
        .join(", ") ||
      [
        header.branchAddressPart1,
        header.branchAddressPart2,
        header.branchCity,
        header.branchState,
        header.branchPincode,
      ]
        .filter(Boolean)
        .join(", ");
    const corrAddr =
      companyInfo.correspondenceAddress ||
      companyInfo.CorrespondenceAddress ||
      companyInfo.addressPart2 ||
      companyInfo.AddressPart2 ||
      header.branchAddressPart2 ||
      "";

    const bankName = companyInfo.bankName || companyInfo.BankName || header.bankName || header.BankName || "";
    const branchName = companyInfo.branchName || companyInfo.BranchName || header.bankBranchName || header.BankBranchName || "";
    const accountNo = companyInfo.accountNumber || companyInfo.accountNo || companyInfo.AccountNumber || header.accountNumber || header.accountNo || header.AccountNo || "";
    const ifscCode = companyInfo.ifscCode || companyInfo.rtgsCode || companyInfo.IfscCode || header.bankIfscCode || header.BankIfscCode || "";
    const termsAndConditions = header.termsAndConditions || companyInfo.termsAndConditions || data.termsAndConditions || "";

    const logoRaw =
      companyInfo.logoServerImage ||
      companyInfo.LogoServerImage ||
      companyInfo.logoPath ||
      companyInfo.LogoPath ||
      companyInfo.logo ||
      companyInfo.Logo ||
      companyInfo.logoData ||
      companyInfo.LogoData ||
      header.logoServerImage ||
      header.LogoServerImage ||
      "";
    const logoSrc = logoRaw
      ? logoRaw.startsWith("data:") || logoRaw.startsWith("http://") || logoRaw.startsWith("https://")
        ? logoRaw
        : `data:image/png;base64,${logoRaw}`
      : "";

    return (
      <div id={nodeId} style={{ display: "flex", flexDirection: "column", gap: compact ? "0" : "20px" }}>
        {copyTypes.map((copyLabel, copyIdx) => (
          <React.Fragment key={copyLabel}>
            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* PAGE 1: FIXED SUMMARY PAGE */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <div
              style={{
                ...ROOT_PAGE,
                fontFamily: "Arial, sans-serif",
                ...(compact ? { pageBreakAfter: "always", breakAfter: "page" } : {}),
              }}
            >
              {/* Spacer for top margin / letterhead (without printed company header) */}
              <div style={{ height: `calc(${S(72)} + 0.5cm)`, width: "100%", marginBottom: "4px" }} />

              {/* ── Bill To & Meta Box ── */}
              <div style={{ border: BORDER, marginBottom: "8px" }}>
                {/* Header Title Bar */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: BORDER,
                    padding: "3px 8px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <div
                    style={{
                      flex: "1 1 30%",
                      fontSize: S(9.5),
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: primaryColor,
                    }}
                  >
                    BILL TO
                  </div>
                  <div
                    style={{
                      flex: "1 1 40%",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: S(13),
                      letterSpacing: "1px",
                      color: primaryColor,
                    }}
                  >
                    TAX INVOICE
                  </div>
                  <div
                    style={{
                      flex: "1 1 30%",
                      textAlign: "right",
                      fontSize: S(9.5),
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      color: primaryColor,
                    }}
                  >
                    {copyLabel}
                  </div>
                </div>

                {/* Inner Content Row */}
                <div style={{ display: "flex", padding: "6px 8px" }}>
                  {/* Buyer Details */}
                  <div style={{ flex: "0 0 58%", paddingRight: "10px" }}>
                    <div style={{ fontWeight: "bold", fontSize: S(10.5), marginBottom: "3px" }}>
                      {buyerName || "N/A"}
                    </div>
                    {buyerAddressLines.length > 0 ? (
                      buyerAddressLines.map((line: string, idx: number) => (
                        <div key={idx} style={{ fontSize: S(9), lineHeight: 1.35 }}>
                          {line}
                        </div>
                      ))
                    ) : buyerAddress ? (
                      <div style={{ fontSize: S(9), lineHeight: 1.35 }}>{buyerAddress}</div>
                    ) : null}
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: S(9), marginTop: "4px" }}>
                      {buyerGstin && (
                        <div>
                          <b>GSTIN No. : </b>
                          <b>{buyerGstin}</b>
                        </div>
                      )}
                      {placeOfSupply && (
                        <div>
                          <b>Place of Supply : </b>
                          <span>{placeOfSupply}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Invoice Metadata */}
                  <div style={{ flex: "0 0 42%", borderLeft: BORDER, paddingLeft: "12px" }}>
                    {[
                      { l: "Invoice #:", v: saleNumber || "N/A", bold: true },
                      { l: "Date:", v: invoiceDate || "N/A" },
                      ...(dueDate ? [{ l: "Due On Date:", v: dueDate }] : []),
                      ...(companyInfo.financialYear ? [{ l: "Financial Year:", v: companyInfo.financialYear }] : []),
                      ...(hsnCode || taxCode ? [{ l: "HSN/SAC:", v: hsnCode || taxCode }] : []),
                    ].map((r) => (
                      <div key={r.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: S(9.5) }}>
                        <b style={{ whiteSpace: "nowrap" }}>{r.l}</b>
                        <span style={{ fontWeight: (r as any).bold ? "bold" : "normal", textAlign: "right" }}>{r.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* ── Summary Item Table ── */}
              <div style={{ border: BORDER, marginBottom: "8px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f9f9f9", borderBottom: BORDER }}>
                      <th
                        style={{
                          width: "22%",
                          padding: "4px 6px",
                          textAlign: "left",
                          fontSize: S(9),
                          fontWeight: "bold",
                          borderRight: BORDER,
                        }}
                      >
                        Product Name
                      </th>
                      <th
                        style={{
                          width: "58%",
                          padding: "4px 6px",
                          textAlign: "left",
                          fontSize: S(9),
                          fontWeight: "bold",
                          borderRight: BORDER,
                        }}
                      >
                        Narration
                      </th>
                      <th
                        style={{
                          width: "20%",
                          padding: "4px 6px",
                          textAlign: "right",
                          fontSize: S(9),
                          fontWeight: "bold",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          padding: "6px",
                          textAlign: "left",
                          fontSize: S(9),
                          fontWeight: "bold",
                          borderRight: BORDER,
                          verticalAlign: "top",
                        }}
                      >
                        
                        {/* {header.productName || header.ProductName || details[0]?.productName || details[0]?.ProductName || "Transportation"} */}
                      </td>
                      <td
                        style={{
                          padding: "6px",
                          textAlign: "left",
                          fontSize: S(9),
                          borderRight: BORDER,
                          verticalAlign: "top",
                        }}
                      >
                        {header.transportationNarration ||
                           
                          ""}
                      </td>
                      <td
                        style={{
                          padding: "6px",
                          textAlign: "right",
                          fontSize: S(9),
                          fontWeight: "bold",
                          verticalAlign: "top",
                        }}
                      >
                        {fmt2(subTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Blank center flex area */}
              <div style={{ flex: "1 1 auto" }} />

              {/* ── Tax Summary + Totals Box ── */}
              <div style={{ border: BORDER, marginBottom: "8px" }}>
                <div style={{ display: "flex" }}>
                  {/* Left: Tax Summary */}
                  <div style={{ flex: "0 0 58%", padding: "6px 8px", borderRight: BORDER, boxSizing: "border-box" }}>
                    <div style={{ fontSize: S(8.5), fontWeight: "bold", textTransform: "uppercase", marginBottom: "3px", color: accentColor }}>Tax Summary</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", border: BORDER }}>
                      <thead>
                        <tr>
                          <th style={TH}>Taxable Amt</th>
                          {isIntraState && (
                            <>
                              <th style={{ ...TH, textAlign: "right" }}>CGST</th>
                              <th style={{ ...TH, textAlign: "right" }}>SGST</th>
                            </>
                          )}
                          {!isIntraState && <th style={{ ...TH, textAlign: "right" }}>IGST</th>}
                          <th style={{ ...TH, textAlign: "right", borderRight: "none" }}>Tax Amt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxList.length > 0 ? (
                          taxList.map((item: any, idx: number) => {
                            const cA = Number(item.totalCentralTaxAmount || 0);
                            const sA = Number(item.totalStateTaxAmount || 0);
                            const iA = Number(item.totalIntegratedTaxAmount || 0);
                            const cP = Number(item.centralTax || 0);
                            const sP = Number(item.stateTax || 0);
                            const iP = Number(item.integratedTax || 0);
                            return (
                              <tr key={idx}>
                                <td style={TD}>{fmt2(item.totalTaxableAmount || 0)}</td>
                                {isIntraState && (
                                  <>
                                    <td style={{ ...TD, textAlign: "right" }}>{cA > 0 ? `${cP}% (${fmt2(cA)})` : "-"}</td>
                                    <td style={{ ...TD, textAlign: "right" }}>{sA > 0 ? `${sP}% (${fmt2(sA)})` : "-"}</td>
                                  </>
                                )}
                                {!isIntraState && <td style={{ ...TD, textAlign: "right" }}>{iA > 0 ? `${iP}% (${fmt2(iA)})` : "-"}</td>}
                                <td style={{ ...TD, textAlign: "right", fontWeight: "bold", borderRight: "none" }}>{fmt2(totalTax)}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td style={TD}>{fmt2(totalTaxableA)}</td>
                            {isIntraState && (
                              <>
                                <td style={{ ...TD, textAlign: "right" }}>{totalCGST > 0 ? `${cgstRate}% (${fmt2(totalCGST)})` : "-"}</td>
                                <td style={{ ...TD, textAlign: "right" }}>{totalSGST > 0 ? `${sgstRate}% (${fmt2(totalSGST)})` : "-"}</td>
                              </>
                            )}
                            {!isIntraState && <td style={{ ...TD, textAlign: "right" }}>{totalIGST > 0 ? `${taxRate}% (${fmt2(totalIGST)})` : "-"}</td>}
                            <td style={{ ...TD, textAlign: "right", fontWeight: "bold", borderRight: "none" }}>{fmt2(totalTax)}</td>
                          </tr>
                        )}
                        <tr style={{ backgroundColor: tableHeaderBg }}>
                          <td style={TH}>{fmt2(totalTaxableA)}</td>
                          {isIntraState && (
                            <>
                              <td style={{ ...TH, textAlign: "right" }}>{fmt2(totalCGST)}</td>
                              <td style={{ ...TH, textAlign: "right" }}>{fmt2(totalSGST)}</td>
                            </>
                          )}
                          {!isIntraState && <td style={{ ...TH, textAlign: "right" }}>{fmt2(totalIGST)}</td>}
                          <td style={{ ...TH, textAlign: "right", borderRight: "none" }}>{fmt2(totalTax)}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div style={{ marginTop: "4px", fontSize: S(9) }}>
                      <div><b>Tax Amount (in words):</b> {convertNumberToWords(totalTax)}</div>
                      <div style={{ marginTop: "2px" }}><b>Net Amount (in words):</b> {convertNumberToWords(netTotal)}</div>
                    </div>
                  </div>

                  {/* Right: Net Amount Summary */}
                  <div style={{ flex: "0 0 42%", padding: "6px 10px", boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div>
                      {[
                        { l: "Sub Total:", v: fmt2(subTotal) },
                        { l: "Shipping Charges:", v: fmt2(shipping) },
                        ...(Math.abs(lrAdjustmentAmount) > 0.01 || Math.abs(shipping) > 0.01 ? [{ l: "Taxable Amount:", v: fmt2(totalTaxableA || (subTotal + shipping)) }] : []),
                        { l: "GST Amount:", v: fmt2(totalTax) },
                        ...(Math.abs(additionalChargesLableValue) > 0.01 ? [{ l: `${additionalChargesLableName}:`, v: fmt2(additionalChargesLableValue) }] : []),
                        ...(Math.abs(roundOff) > 0.01 ? [{ l: "Round Off:", v: fmt2(roundOff) }] : []),
                      ].map((r) => (
                        <div key={r.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                          <span style={{ fontSize: S(9.5), color: "#555" }}>{r.l}</span>
                          <span style={{ fontSize: S(9.5) }}>{r.v}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", borderTop: `2px solid ${primaryColor}`, paddingTop: "4px", marginTop: "4px" }}>
                      <span style={{ fontSize: S(11), fontWeight: "bold", color: primaryColor }}>Net Amount:</span>
                      <span style={{ fontSize: S(11), fontWeight: "bold", color: primaryColor }}>₹{fmt2(netTotal)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Footer Box ── */}
              <div style={{ border: BORDER, padding: "6px 8px" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  {/* Notes & Declaration */}
                  <div style={{ flex: "1 1 auto" }}>
                    <div style={{ fontSize: S(8.5), fontWeight: "bold", color: accentColor, marginBottom: "2px" }}>Customer Notes</div>
                    <div style={{ fontSize: S(8.5), color: "#666", fontStyle: "italic", marginBottom: "3px" }}>
                      Thank you for your business. Please process this invoice within terms.
                    </div>
                    <div style={{ fontSize: S(8.5), fontWeight: "bold" }}>
                      SUBJECT TO {branchName?.toUpperCase() || firmName.toUpperCase() || ""} JURISDICTION
                    </div>
                    <div style={{ fontSize: S(8), color: "#666", marginBottom: "4px" }}>E &amp; O.E</div>
                    <div style={{
                      borderTop: "1px dashed #ccc",
                      paddingTop: "4px",
                      fontSize: S(7.5),
                      lineHeight: 1.35,
                      color: "#444",
                      textAlign: "justify"
                    }}>
                      <b>DECLARATION:</b> "We have taken registration under the CGST Act, 2017 and have exercised the option to pay tax on services of GTA in relation to transport of goods supplied by us during the Financial Year 2026-27 under forward charge."
                    </div>
                  </div>
                  {/* QR */}
                  {upiId && (
                    <div style={{ flex: "0 0 14%", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold" }}>UPI: {upiId}</div>
                      <img src={qrUrl} alt="QR" style={{ width: 55, height: 55, border: BORDER, borderRadius: "0px" }} />
                      <div style={{ fontSize: S(8), color: "#666" }}>Scan to Pay</div>
                    </div>
                  )}
                  {/* Bank Details */}
                  <div style={{ flex: "0 0 24%", fontSize: S(8), lineHeight: 1.35 }}>
                    <div style={{ fontSize: S(8.5), fontWeight: "bold", color: accentColor, marginBottom: "3px", textTransform: "uppercase" }}>Bank Details</div>
                    {(accountNo || accountName) && <div><b>A/C No:</b> {accountNo || accountName}</div>}
                    {accountHolderName && <div><b>Holder:</b> {accountHolderName}</div>}
                    {bankName && <div><b>Bank:</b> {bankName}</div>}
                    {ifscCode && <div><b>IFSC:</b> {ifscCode}</div>}
                    {branchName && <div><b>Branch:</b> {branchName}</div>}
                    {bankAddress && <div style={{ fontSize: S(7.5), color: "#555" }}>{bankAddress}</div>}
                  </div>
                  {/* Signature */}
                  <div style={{ flex: "0 0 20%", textAlign: "right", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: "90px", alignItems: "flex-end" }}>
                    <div style={{ fontSize: S(10.5), fontWeight: "bold", color: primaryColor }}>
                      For {firmName}
                    </div>
                    {(companyInfo.signatureServerImage || companyInfo.SignatureServerImage) && (
                      <img src={`data:image/png;base64,${companyInfo.signatureServerImage || companyInfo.SignatureServerImage}`}
                        alt="Signature" style={{ height: 40, maxWidth: 100, objectFit: "contain", margin: "4px 0" }} />
                    )}
                    <div style={{ fontSize: S(9), color: "#555", fontStyle: "italic", borderTop: "1px dashed #000", paddingTop: "3px", width: "100px", textAlign: "center" }}>
                      Authorised Signature
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* PAGE 2: DETAILED STATEMENT TABLE PAGE */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <div
              style={{
                ...ROOT_PAGE,
                ...(compact
                  ? {
                      pageBreakAfter: copyIdx === copyTypes.length - 1 ? "auto" : "always",
                      breakAfter: copyIdx === copyTypes.length - 1 ? "auto" : "page",
                    }
                  : {}),
              }}
            >
              <div>
                {/* Page 2 Top Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    borderBottom: BORDER_BOLD,
                    paddingBottom: "4px",
                    marginBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", fontSize: S(18), color: "#000" }}>{firmName}</div>
                  {companyGstin && <div style={{ fontWeight: "bold", fontSize: S(10) }}>GSTIN No.: {companyGstin}</div>}
                </div>
                {officeAddr && <div style={{ fontSize: S(8.5), marginBottom: "8px" }}>{officeAddr}</div>}

                {/* Buyer & Invoice Header Bar */}
                <div style={{ border: BORDER, padding: "6px 8px", marginBottom: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: S(9.5) }}>
                    <div>
                      <div style={{ fontWeight: "bold" }}>To,</div>
                      <div style={{ fontWeight: "bold", fontSize: S(10.5) }}>{buyerName}</div>
                      {buyerAddress && <div style={{ fontSize: S(9) }}>{buyerAddress}</div>}
                    </div>
                    {buyerGstin && (
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontWeight: "bold" }}>GSTIN No.: {buyerGstin}</div>
                      </div>
                    )}
                    <div style={{ textAlign: "right" }}>
                      <div>
                        <span style={{ fontWeight: "bold" }}>Inv No.: </span>
                        <span style={{ fontWeight: "bold" }}>{saleNumber}</span>
                      </div>
                      <div>
                        <span style={{ fontWeight: "bold" }}>Date: </span>
                        <span>{invoiceDate}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statement Banner */}
                <div
                  style={{
                    border: BORDER,
                    backgroundColor: "#f2f2f2",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: S(11),
                    padding: "4px 8px",
                    marginBottom: "8px",
                  }}
                >
                  Transportation Details 
                </div>

                {/* Detailed Statement Table */}
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: BORDER,
                    fontSize: S(9),
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: BORDER, backgroundColor: "#f2f2f2", textAlign: "center" }}>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5) }}>LR No</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5) }}>Invoice No</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5) }}>LR Date</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5), textAlign: "left" }}>Tanker No.</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5), textAlign: "left" }}>Product</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5), textAlign: "left" }}>Bill From</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5), textAlign: "left" }}>Ship To</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5), textAlign: "right" }}>Actual Qty.</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5), textAlign: "right" }}>Bill Qty.</th>
                      <th style={{ padding: "3px 4px", borderRight: BORDER, fontWeight: "bold", fontSize: S(9.5), textAlign: "right" }}>Rate</th>
                      <th style={{ padding: "3px 4px", fontWeight: "bold", fontSize: S(9.5), textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.flatMap((item: any, idx: number) => {
                      const tripDate = fmtDate(item.lrDate || item.LrDate || item.tripDate || item.TripDate);
                      const lrNo = item.lrNumber || item.LrNumber || item.lrNo || item.LrNo || "-";
                      const invNo = item.invoiceRefNo || item.InvoiceRefNo || item.invoiceNo || item.InvoiceNo || item.challanNo || item.ChallanNo || "";
                      const tankerNo = item.vehicleNo || item.VehicleNo || item.tankerNo || item.TankerNo || "-";
                      const product = item.productName || item.ProductName || item.product || item.Product || "-";
                      const fromParty = item.billFromPartyName || item.BillFromPartyName || "";
                      const fromLoc = item.billFrom || item.BillFrom || item.from || item.From || "";
                      const toParty = item.shipToPartyName || item.ShipToPartyName || "";
                      const toLoc = item.shipTo || item.ShipTo || item.to || item.To || "";
                      const actQty = Number(item.actualQuantity || item.ActualQuantity || 0);
                      const billQty = Number(item.billQuantity || item.BillQuantity || 0);
                      const rate = Number(item.unitPrice || item.UnitPrice || item.rate || item.Rate || 0);
                      const itemAmt = Number(item.finalAmount || item.FinalAmount || 0);
                      const waitingAmt = Number(item.waitingCharges || item.WaitingCharges || 0);

                      const rows = [];
                      rows.push(
                        <tr key={`main-${idx}`} style={{ borderBottom: BORDER, fontWeight: 500 }}>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "center" }}>{lrNo}</td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "center" }}>{invNo || "-"}</td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "center" }}>{tripDate || "-"}</td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "left" }}>{tankerNo}</td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "left" }}>{product}</td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "left" }}>
                            {fromLoc || fromParty || "-"}
                          </td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "left" }}>
                            {toLoc || toParty || "-"}
                          </td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "right" }}>
                            {actQty.toFixed(3)}
                          </td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "right" }}>
                            {billQty.toFixed(3)}
                          </td>
                          <td style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "right" }}>
                            {rate.toFixed(2)}
                          </td>
                          <td style={{ padding: "3px 4px", textAlign: "right", fontWeight: "bold" }}>{formatIndianCurrency(itemAmt)}</td>
                        </tr>
                      );

                      if (waitingAmt > 0) {
                        rows.push(
                          <tr key={`waiting-${idx}`} style={{ borderBottom: BORDER, fontWeight: 500 }}>
                            <td colSpan={10} style={{ padding: "3px 4px", borderRight: BORDER, textAlign: "right", color: "#555", fontSize: S(8) }}>
                              <i>No. of days = {Number(item.numberOfDays || item.NumberOfDays || 0)} * per day Amount = {Number(item.perDayAmount || item.PerDayAmount || 0).toFixed(2)} = Waiting Charges:</i>
                            </td>
                            <td style={{ padding: "3px 4px", textAlign: "right", fontWeight: "bold", fontSize: S(8.5) }}>
                              {formatIndianCurrency(waitingAmt)}
                            </td>
                          </tr>
                        );
                      }

                      return rows;
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: BORDER_BOLD, fontWeight: "bold", backgroundColor: "#f9f9f9" }}>
                      <td colSpan={7} style={{ padding: "4px 5px", textAlign: "right", borderRight: BORDER, fontSize: S(9.5) }}>
                        Total
                      </td>
                      <td style={{ padding: "4px 5px", textAlign: "right", borderRight: BORDER, fontSize: S(9.5) }}>
                        {totalActQty.toFixed(3)}
                      </td>
                      <td style={{ padding: "4px 5px", textAlign: "right", borderRight: BORDER, fontSize: S(9.5) }}>
                        {totalBillQty.toFixed(3)}
                      </td>
                      <td style={{ padding: "4px 5px", borderRight: BORDER }}></td>
                      <td style={{ padding: "4px 5px", textAlign: "right", fontSize: S(9.5) }}>
                        {formatIndianCurrency(totalAmt)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Bottom Spacer */}
              <div style={{ height: "10px" }} />
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: printCSS }} />

      {/* Hidden print container used for window.print() and html2canvas PDF download */}
      <div id="transport-details-report-print-node" style={{ display: "none" }}>
        {renderInvoice("transport-details-report-print-inner", true)}
      </div>

      {isMobile ? (
        <Box
          className="no-print"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: "#f8f9fa",
          }}
        >
          {/* Mobile Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #dfe1e5",
              bgcolor: "#fff",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#202124" }}>
              Invoice Actions
            </Typography>
            <IconButton onClick={onClose} size="medium" sx={{ color: "grey.700" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Action Area */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 3,
              gap: 2.5,
              overflowY: "auto",
            }}
          >
            {/* Invoice Info */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1a73e8", mb: 0.5 }}>
                {header.saleNumber || header.SaleNumber || "Invoice"}
              </Typography>
              {(header.buyerName || header.BuyerName) && (
                <Typography variant="body2" sx={{ color: "grey.600" }}>
                  {header.buyerName || header.BuyerName}
                </Typography>
              )}
              {netTotal !== undefined && (
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "#202124", mt: 1 }}>
                  ₹{formatIndianCurrency(netTotal)}
                </Typography>
              )}
            </Box>

            <Box sx={{ width: "100%", maxWidth: "320px", display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" sx={{ color: "grey.600", fontWeight: "bold" }}>
                Select Invoice Copy
              </Typography>
              <Select
                value={selectedCopy}
                onChange={(e) => setSelectedCopy(e.target.value as string)}
                size="small"
                fullWidth
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dadce0" },
                }}
              >
                <MenuItem value="Both">Both Copies</MenuItem>
                <MenuItem value="Original">Original Copy</MenuItem>
                <MenuItem value="Duplicate">Duplicate Copy</MenuItem>
              </Select>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ width: "100%", maxWidth: "320px", display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {handleClickOpenSendEmail && (
                <Button
                  variant="outlined"
                  onClick={() => handleClickOpenSendEmail(true, itemData)}
                  startIcon={<SendIcon />}
                  fullWidth
                  sx={{ py: 1.5, borderRadius: "8px", color: "#1976d2", textTransform: "none", fontWeight: "bold" }}
                >
                  Send Email
                </Button>
              )}
              {handleClickOpenSendWhatsapp && (
                <Button
                  variant="outlined"
                  onClick={() => handleClickOpenSendWhatsapp(true, itemData)}
                  startIcon={<WhatsAppIcon />}
                  fullWidth
                  sx={{ py: 1.5, borderRadius: "8px", color: "#25D366", textTransform: "none", fontWeight: "bold" }}
                >
                  Send WhatsApp
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: "8px",
                  bgcolor: "#1a73e8",
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "15px",
                  boxShadow: "0 2px 4px rgba(26,115,232,0.2)",
                  "&:hover": { bgcolor: "#1557b0" },
                }}
              >
                {isDownloading ? "Downloading PDF..." : "Download PDF"}
              </Button>
              <Button
                variant="outlined"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: "8px",
                  borderColor: "#dadce0",
                  color: "#3c4043",
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "15px",
                  bgcolor: "#fff",
                  "&:hover": { borderColor: "#bdc1c6", bgcolor: "#f8f9fa" },
                }}
              >
                Print Invoice
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {/* Toolbar */}
          <Box
            className="no-print"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderBottom: "1px solid #dfe1e5",
              bgcolor: "#f8f9fa",
              height: "56px",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton size="small" sx={{ color: "grey.700" }}>
                <MenuOpenIcon />
              </IconButton>
              <Box sx={{ display: "flex", alignItems: "center", borderLeft: "1px solid #dadce0", pl: 1.5, gap: 1.5 }}>
                <Select
                  value={selectedCopy}
                  onChange={(e) => setSelectedCopy(e.target.value as string)}
                  size="small"
                  sx={{
                    height: "32px",
                    fontSize: "13px",
                    bgcolor: "#fff",
                    minWidth: "125px",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dadce0" },
                  }}
                >
                  <MenuItem value="Both">Both Copies</MenuItem>
                  <MenuItem value="Original">Original Copy</MenuItem>
                  <MenuItem value="Duplicate">Duplicate Copy</MenuItem>
                </Select>
                <Select
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  size="small"
                  sx={{
                    height: "32px",
                    fontSize: "13px",
                    bgcolor: "#fff",
                    minWidth: "85px",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dadce0" },
                  }}
                >
                  {[50, 75, 100, 125, 150, 175, 200].map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}%
                    </MenuItem>
                  ))}
                </Select>
                <IconButton
                  size="small"
                  onClick={() => setZoom((p) => Math.max(p - 10, 50))}
                  disabled={zoom <= 50}
                  sx={{ border: "1px solid #dadce0", bgcolor: "#fff" }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setZoom((p) => Math.min(p + 10, 200))}
                  disabled={zoom >= 200}
                  sx={{ border: "1px solid #dadce0", bgcolor: "#fff" }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Action Toolbar Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {handleClickOpenSendEmail && (
                <IconButton
                  onClick={() => handleClickOpenSendEmail(true, itemData)}
                  size="medium"
                  sx={{ color: "#1976d2" }}
                  title="Send Email"
                >
                  <SendIcon />
                </IconButton>
              )}
              {handleClickOpenSendWhatsapp && (
                <IconButton
                  onClick={() => handleClickOpenSendWhatsapp(true, itemData)}
                  size="medium"
                  sx={{ color: "#25D366" }}
                  title="Send WhatsApp"
                >
                  <WhatsAppIcon />
                </IconButton>
              )}
              <IconButton onClick={handlePrint} size="medium" sx={{ color: "grey.700" }} title="Print">
                <PrintIcon />
              </IconButton>
              <IconButton
                onClick={handleDownloadPDF}
                size="medium"
                sx={{ color: "grey.700" }}
                title="Download PDF"
                disabled={isDownloading}
              >
                {isDownloading ? <CircularProgress size={20} /> : <GetAppIcon />}
              </IconButton>
              <Box sx={{ borderLeft: "1px solid #dadce0", ml: 1, pl: 1 }}>
                <IconButton onClick={onClose} size="medium" sx={{ color: "grey.700" }} title="Close">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Canvas Area */}
          <DialogContent
            className="no-print"
            sx={{
              p: 0,
              bgcolor: "#525659",
              display: "flex",
              justifyContent: "center",
              overflowY: "auto",
              overflowX: "hidden",
              flex: 1,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ py: 3, px: 2, width: "100%", display: "flex", justifyContent: "center", boxSizing: "border-box" }}>
              <Box
                id="transport-details-report-preview-node"
                sx={{
                  width: "min(100%, 794px)",
                  flexShrink: 0,
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: "top center",
                  transition: "transform 0.12s ease-out",
                  mb: `${((zoom / 100) - 1) * 100}%`,
                }}
              >
                {renderInvoice("transport-details-report-preview-inner", false)}
              </Box>
            </Box>
          </DialogContent>
        </>
      )}
    </>
  );
}
